import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Thermometer, Plus, Check, X, Edit, Trash2, Printer, 
  Download, RefreshCw, Wifi, Battery, Unlink, ChevronLeft, ChevronRight,
  Zap, AlertTriangle, Calendar, Link2, Clock, Settings, Play, Power, Wrench
} from 'lucide-react';
import { getApiHeaders } from '../../utils/apiHelpers';
import MonitoraggioTemperature from './MonitoraggioTemperature';
import { TemperatureDaScrivere } from './frigoriferi/TemperatureDaScrivere';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Usa l'helper centralizzato per gli headers
const getAuthHeaders = getApiHeaders;

// Stato operativo frigorifero: spento / manutenzione = escluso da registro, verifica 4h e allarmi
const STATI_FRIGO = {
  attivo: { label: 'Attivo', badge: 'bg-green-100 text-green-700', Icon: Check },
  spento: { label: 'Spento', badge: 'bg-gray-200 text-gray-700', Icon: Power },
  manutenzione: { label: 'In manutenzione', badge: 'bg-amber-100 text-amber-700', Icon: Wrench },
};
const statoDi = (frigo) => frigo?.stato_operativo || 'attivo';

// Colore del valore in registro: sensore muto = ambra, spento/manutenzione = grigio
const coloreRegistrazione = (reg) => {
  if (reg.stato_operativo === 'non_raggiungibile') return 'text-amber-400';
  if (reg.stato_operativo) return 'text-gray-400';
  return reg.in_range ? 'text-green-400' : 'text-red-400';
};

const avvisaNonRaggiungibili = (lista) => {
  if (lista?.length > 0) {
    toast.warning(`Sensore non raggiungibile (controlla le pile): ${lista.join(', ')}`, { duration: 10000 });
  }
};
const isEscluso = (frigo) => statoDi(frigo) !== 'attivo';

const StatoFrigoBadge = ({ frigo }) => {
  const s = STATI_FRIGO[statoDi(frigo)];
  if (!frigo || statoDi(frigo) === 'attivo') return null;
  return (
    <span data-testid={`frigo-stato-badge-${frigo.id}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase ${s.badge}`}>
      <s.Icon className="h-3 w-3" /> {s.label}
    </span>
  );
};

const StatoFrigoModal = ({ frigo, form, setForm, onSave, onClose, saving }) => (
  <div className="fixed inset-0 bg-black/70 flex items-end lg:items-center justify-center z-[140]" data-testid="stato-frigo-modal">
    <div className="bg-white rounded-t-2xl lg:rounded-2xl w-full lg:max-w-md shadow-2xl">
      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-gray-900">Stato apparecchio</h3>
          <p className="text-xs text-gray-500">{frigo.nome}</p>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg" aria-label="Chiudi"><X className="h-5 w-5 text-gray-500" /></button>
      </div>
      <div className="p-4 space-y-3">
        {Object.entries(STATI_FRIGO).map(([key, s]) => (
          <button
            key={key}
            type="button"
            data-testid={`stato-frigo-opzione-${key}`}
            onClick={() => setForm({ ...form, stato: key })}
            className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-colors ${
              form.stato === key ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200 hover:border-gray-300'}`}
          >
            <s.Icon className={`h-5 w-5 ${key === 'attivo' ? 'text-green-600' : key === 'spento' ? 'text-gray-600' : 'text-amber-600'}`} />
            <div className="flex-1">
              <p className="font-medium text-gray-900 text-sm">{s.label}</p>
              <p className="text-xs text-gray-500">
                {key === 'attivo' ? 'Letture automatiche alla compilazione del registro e allarmi attivi'
                  : 'Escluso da registro automatico e allarmi (nel registro comparirà come ' + s.label.toUpperCase() + ')'}
              </p>
            </div>
          </button>
        ))}
        {form.stato !== 'attivo' && (
          <textarea
            data-testid="stato-frigo-note"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="Note (facoltative): es. compressore in riparazione"
            rows={2}
            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-gray-900"
          />
        )}
        {frigo.stato_dal && isEscluso(frigo) && (
          <p className="text-xs text-gray-500">Stato attuale dal {new Date(frigo.stato_dal).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}{frigo.stato_note ? ` — ${frigo.stato_note}` : ''}</p>
        )}
      </div>
      <div className="p-4 border-t border-gray-100 flex gap-2">
        <button onClick={onClose} className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium text-sm">Annulla</button>
        <button data-testid="stato-frigo-salva" onClick={onSave} disabled={saving} className="flex-1 py-2.5 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-60 text-white rounded-xl font-medium text-sm">
          {saving ? 'Salvo...' : 'Salva stato'}
        </button>
      </div>
    </div>
  </div>
);

// ============ FRIGORIFERI SECTION (COMPLETA) ============
const FrigoriferiSection = () => {
  const [frigoriferi, setFrigoriferi] = useState([]);
  const [registrazioni, setRegistrazioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddFrigo, setShowAddFrigo] = useState(false);
  const [showRegistraTemp, setShowRegistraTemp] = useState(false);
  const [selectedFrigo, setSelectedFrigo] = useState(null);
  const [statoOggi, setStatoOggi] = useState([]);
  
  // Filtri
  const [filterAnno, setFilterAnno] = useState(new Date().getFullYear());
  const [filterMese, setFilterMese] = useState(new Date().getMonth() + 1);
  const [filterFrigo, setFilterFrigo] = useState('');
  
  // Form nuovo frigorifero
  const [nuovoFrigo, setNuovoFrigo] = useState({
    nome: '',
    tipo: 'frigorifero',
    posizione: '',
    temp_min: 0,
    temp_max: 4,
    sensore_email: '',
    note: ''
  });
  
  // Form registrazione temperatura
  const [regTemp, setRegTemp] = useState({
    temperatura: '',
    operatore: '',
    note: '',
    azione_correttiva: ''
  });

  // Parametri periodo per i PDF (registro temperature / registro anomalie)
  const buildPrintParams = () => {
    const params = new URLSearchParams();
    if (printType === 'mese') {
      params.set('anno', printAnno);
      params.set('mese', printMese);
    } else if (printType === 'anno') {
      params.set('anno', printAnno);
    } else {
      params.set('data_da', printDataDa);
      params.set('data_a', printDataA);
    }
    const tenantDb = localStorage.getItem('tenant_db');
    if (tenantDb) params.set('tenant', tenantDb.replace(/^(tenant_)+/, ''));
    return params;
  };
  const [firmaCanvas, setFirmaCanvas] = useState(null);
  const canvasRef = React.useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  const [saving, setSaving] = useState(false);
  const [editingFrigo, setEditingFrigo] = useState(null);
  
  // Modal stampa
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printType, setPrintType] = useState('mese'); // 'mese', 'range', 'anno'
  const [printAnno, setPrintAnno] = useState(new Date().getFullYear());
  const [printMese, setPrintMese] = useState(new Date().getMonth() + 1);
  const [printDataDa, setPrintDataDa] = useState('');
  const [printDataA, setPrintDataA] = useState('');
  const [printing, setPrinting] = useState(false);
  
  // Modal etichette frigoriferi
  const [showEtichetteModal, setShowEtichetteModal] = useState(false);
  const [frigoriferiSelezionatiEtichette, setFrigoriferiSelezionatiEtichette] = useState([]);
  
  // Modal elenco frigoriferi per tipo
  const [showListaTipo, setShowListaTipo] = useState(null); // 'frigorifero' o 'congelatore'
  const [statoFrigoModal, setStatoFrigoModal] = useState(null); // frigo di cui cambiare lo stato operativo
  const [statoForm, setStatoForm] = useState({ stato: 'attivo', note: '' });
  const [salvandoStato, setSalvandoStato] = useState(false);

  const apriStatoFrigo = (frigo) => {
    setStatoForm({ stato: statoDi(frigo), note: frigo.stato_note || '' });
    setStatoFrigoModal(frigo);
  };

  const salvaStatoFrigo = async () => {
    if (!statoFrigoModal) return;
    setSalvandoStato(true);
    try {
      const res = await axios.patch(`${API}/gestione/haccp/frigoriferi/${statoFrigoModal.id}/stato`,
        { stato: statoForm.stato, note: statoForm.note }, { headers: getAuthHeaders() });
      toast.success(`${statoFrigoModal.nome}: ${res.data.label || statoForm.stato}`);
      setStatoFrigoModal(null);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio stato');
    } finally {
      setSalvandoStato(false);
    }
  };
  
  // Registrazione per giorno specifico
  const [dataRegistrazione, setDataRegistrazione] = useState(null); // Data specifica per registrazione
  const [showSelezionaFrigo, setShowSelezionaFrigo] = useState(false); // Modal per scegliere frigo
  
  // Giorno selezionato nel calendario per visualizzare dettagli
  const [giornoSelezionato, setGiornoSelezionato] = useState(null);
  const [registrazioniGiorno, setRegistrazioniGiorno] = useState([]);
  
  // Modifica registrazione
  const [editingRegistrazione, setEditingRegistrazione] = useState(null);
  
  // Compilazione rapida sequenziale
  const [frigoriferiDaCompilare, setFrigoriferiDaCompilare] = useState([]); // Lista frigo da compilare in sequenza
  const [indiceFrigoCorrente, setIndiceFrigoCorrente] = useState(0);
  
  // Compilazione massiva
  const [showCompilazioneMassiva, setShowCompilazioneMassiva] = useState(false);
  const [massivaTipo, setMassivaTipo] = useState('mese'); // 'mese' o 'range'
  const [massivaAnno, setMassivaAnno] = useState(new Date().getFullYear());
  const [massivaMese, setMassivaMese] = useState(new Date().getMonth() + 1);
  
  // Sensori Tuya
  const [showSensoriModal, setShowSensoriModal] = useState(false);
  const [sensoriTuya, setSensoriTuya] = useState([]);
  const [loadingSensori, setLoadingSensori] = useState(false);
  const [abbinandoFrigo, setAbbinandoFrigo] = useState(null); // Frigo in fase di abbinamento
  const [massivaDataDa, setMassivaDataDa] = useState('');
  const [massivaDataA, setMassivaDataA] = useState('');
  const [massivaTemp, setMassivaTemp] = useState({});
  const [massivaOperatore, setMassivaOperatore] = useState('');
  const [massivaFrigoSelezionati, setMassivaFrigoSelezionati] = useState([]);
  const [savingMassiva, setSavingMassiva] = useState(false);

  // Impostazioni compilazione automatica
  const [showImpostazioniAuto, setShowImpostazioniAuto] = useState(false);
  const [impostazioniAuto, setImpostazioniAuto] = useState({
    abilitato: false,
    ora: '07:00',
    operatore_default: 'Sistema Automatico',
    giorni_attivi: ['lun', 'mar', 'mer', 'gio', 'ven', 'sab', 'dom'],
    notifica_anomalie: true,
    email_notifiche: ''
  });
  const [savingImpostazioni, setSavingImpostazioni] = useState(false);
  const [eseguendoAuto, setEseguendoAuto] = useState(false);

  useEffect(() => {
    loadData();
    loadImpostazioniAuto();
  }, []);

  useEffect(() => {
    if (filterAnno && filterMese) {
      loadRegistrazioni();
    }
  }, [filterAnno, filterMese, filterFrigo]);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [frigoRes, statoRes] = await Promise.all([
        axios.get(`${API}/gestione/haccp/frigoriferi-con-sensori`, { headers }),
        axios.get(`${API}/gestione/haccp/frigoriferi/stato-oggi`, { headers })
      ]);
      
      setFrigoriferi(frigoRes.data.frigoriferi || []);
      setStatoOggi(statoRes.data.frigoriferi || []);
    } catch (error) {
      console.error('Errore caricamento frigoriferi:', error);
      
    } finally {
      setLoading(false);
    }
  };

  // Carica impostazioni compilazione automatica
  const loadImpostazioniAuto = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/gestione/haccp/frigoriferi/impostazioni-auto`, { headers });
      if (res.data) {
        setImpostazioniAuto(res.data);
      }
    } catch (error) {
      console.error('Errore caricamento impostazioni auto:', error);
    }
  };

  // Salva impostazioni compilazione automatica
  const salvaImpostazioniAuto = async () => {
    setSavingImpostazioni(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/gestione/haccp/frigoriferi/impostazioni-auto`, impostazioniAuto, { headers });
      toast.success('Impostazioni salvate!');
      setShowImpostazioniAuto(false);
    } catch (error) {
      console.error('Errore salvataggio impostazioni:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSavingImpostazioni(false);
    }
  };

  // Esegui compilazione automatica manualmente
  const eseguiCompilazioneAuto = async () => {
    setEseguendoAuto(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/gestione/haccp/frigoriferi/esegui-compilazione-auto`, {}, { headers });
      
      if (res.data.registrazioni?.length > 0) {
        toast.success(`✅ ${res.data.registrazioni.length} registrazioni create`);
      } else {
        toast.info('Nessuna registrazione creata');
      }
      
      if (res.data.errori?.length > 0) {
        toast.warning(`⚠️ ${res.data.errori.length} errori`);
      }
      if (res.data.esclusi?.length > 0) {
        toast.info(`Esclusi (spenti/manutenzione): ${res.data.esclusi.join(', ')}`);
      }
      avvisaNonRaggiungibili(res.data.non_raggiungibili);
      
      await loadData();
      await loadRegistrazioni();
    } catch (error) {
      console.error('Errore compilazione automatica:', error);
      toast.error('Errore nella compilazione automatica');
    } finally {
      setEseguendoAuto(false);
    }
  };
  
  // Carica sensori Tuya
  const loadSensoriTuya = async () => {
    setLoadingSensori(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/gestione/haccp/tuya/dispositivi`, { headers });
      setSensoriTuya(res.data.dispositivi || []);
    } catch (error) {
      console.error('Errore caricamento sensori Tuya:', error);
      toast.error('Errore connessione sensori');
    } finally {
      setLoadingSensori(false);
    }
  };
  
  // Abbina sensore a frigorifero
  const abbinaSensore = async (sensore, usaSondaEsterna = false) => {
    if (!abbinandoFrigo) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/gestione/haccp/frigoriferi/${abbinandoFrigo.id}/abbina-sensore`, {
        sensore_id: sensore.id,
        sensore_nome: sensore.nome,
        usa_sonda_esterna: usaSondaEsterna
      }, { headers });
      
      toast.success(`Sensore "${sensore.nome}" abbinato a "${abbinandoFrigo.nome}"`);
      setAbbinandoFrigo(null);
      setShowSensoriModal(false);
      await loadData();
    } catch (error) {
      console.error('Errore abbinamento sensore:', error);
      toast.error('Errore durante l\'abbinamento');
    }
  };
  
  // Rimuovi sensore da frigorifero
  const rimuoviSensore = async (frigoId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/gestione/haccp/frigoriferi/${frigoId}/abbina-sensore`, { headers });
      
      toast.success('Sensore rimosso');
      await loadData();
    } catch (error) {
      console.error('Errore rimozione sensore:', error);
      toast.error('Errore durante la rimozione');
    }
  };
  
  // Lettura automatica temperature
  const eseguiLetturaAutomatica = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/gestione/haccp/tuya/lettura-automatica`, {}, { headers });
      
      toast.success(`Registrate ${res.data.totale_letti ?? res.data.registrazioni?.length ?? 0} temperature`);
      if (res.data.esclusi?.length > 0) {
        toast.info(`Esclusi (spenti/manutenzione): ${res.data.esclusi.join(', ')}`);
      }
      avvisaNonRaggiungibili(res.data.non_raggiungibili);
      await loadData();
      await loadRegistrazioni();
    } catch (error) {
      console.error('Errore lettura automatica:', error);
      toast.error('Errore durante la lettura');
    }
  };

  const loadRegistrazioni = async () => {
    try {
      const headers = getAuthHeaders();
      const params = new URLSearchParams();
      if (filterAnno) params.append('anno', filterAnno);
      if (filterMese) params.append('mese', filterMese);
      if (filterFrigo) params.append('frigorifero_id', filterFrigo);
      
      const res = await axios.get(`${API}/gestione/haccp/frigoriferi/registrazioni?${params}`, { headers });
      
      setRegistrazioni(res.data.registrazioni || []);
    } catch (error) {
      console.error('Errore caricamento registrazioni:', error);
    }
  };

  const handleAddFrigo = async () => {
    if (!nuovoFrigo.nome) {
      toast.error('Inserisci il nome del frigorifero');
      return;
    }
    
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      
      if (editingFrigo) {
        await axios.put(`${API}/gestione/haccp/frigoriferi/${editingFrigo.id}`, nuovoFrigo, { headers });
        toast.success('Frigorifero aggiornato!');
      } else {
        await axios.post(`${API}/gestione/haccp/frigoriferi`, nuovoFrigo, { headers });
        toast.success('Frigorifero aggiunto!');
      }
      
      setShowAddFrigo(false);
      setEditingFrigo(null);
      setNuovoFrigo({
        nome: '',
        tipo: 'frigorifero',
        posizione: '',
        temp_min: 0,
        temp_max: 4,
        sensore_email: '',
        note: ''
      });
      loadData();
    } catch (error) {
      console.error('Errore salvataggio:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFrigo = async (id) => {
    if (!window.confirm('Disattivare questo frigorifero?')) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/gestione/haccp/frigoriferi/${id}`, { headers });
      toast.success('Frigorifero disattivato');
      loadData();
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const openRegistraTemp = (frigo, dataSpecifica = null) => {
    if (isEscluso(frigo)) {
      toast.error(`${frigo.nome} è ${STATI_FRIGO[statoDi(frigo)].label.toLowerCase()}: registrazione non consentita. Riportalo su "Attivo" per registrare.`);
      return;
    }
    setSelectedFrigo(frigo);
    setDataRegistrazione(dataSpecifica);
    setEditingRegistrazione(null);
    setRegTemp({
      temperatura: '',
      operatore: '',
      note: '',
      azione_correttiva: ''
    });
    setShowRegistraTemp(true);
  };
  
  // Apre modal per scegliere frigorifero per un giorno specifico
  const openRegistraGiorno = (giorno) => {
    const data = new Date(filterAnno, filterMese - 1, giorno);
    setDataRegistrazione(data);
    setFrigoriferiDaCompilare([...frigoriferi]); // Tutti i frigoriferi da compilare
    setIndiceFrigoCorrente(0);
    setShowSelezionaFrigo(true);
  };
  
  // Inizia compilazione rapida di tutti i frigoriferi per un giorno
  const startCompilazioneRapida = () => {
    if (frigoriferi.length === 0) return;
    setIndiceFrigoCorrente(0);
    setShowSelezionaFrigo(false);
    openRegistraTemp(frigoriferi[0], dataRegistrazione);
  };
  
  // Passa al prossimo frigorifero nella compilazione rapida
  const prossimoFrigorifero = () => {
    const nextIndex = indiceFrigoCorrente + 1;
    if (nextIndex < frigoriferiDaCompilare.length) {
      setIndiceFrigoCorrente(nextIndex);
      setRegTemp({ temperatura: '', operatore: regTemp.operatore, note: '', azione_correttiva: '' }); // Mantieni operatore
      setSelectedFrigo(frigoriferiDaCompilare[nextIndex]);
      // Pulisci canvas firma
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    } else {
      // Completato tutti i frigoriferi
      setShowRegistraTemp(false);
      setDataRegistrazione(null);
      setFrigoriferiDaCompilare([]);
      setIndiceFrigoCorrente(0);
      toast.success('Tutti i frigoriferi compilati!');
    }
  };

  const handleRegistraTemp = async () => {
    if (!regTemp.temperatura || !regTemp.operatore) {
      toast.error('Compila temperatura e nome operatore');
      return;
    }
    
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      
      // Ottieni firma dal canvas se presente
      let firma_base64 = null;
      if (canvasRef.current) {
        firma_base64 = canvasRef.current.toDataURL('image/png').split(',')[1];
      }
      
      // Prepara data specifica se presente
      let data_specifica = null;
      if (dataRegistrazione) {
        // Imposta l'ora a mezzogiorno per evitare problemi di fuso orario
        const dataConOra = new Date(dataRegistrazione);
        dataConOra.setHours(12, 0, 0, 0);
        data_specifica = dataConOra.toISOString();
      }
      
      const res = await axios.post(`${API}/gestione/haccp/frigoriferi/registra-temperatura`, {
        frigorifero_id: selectedFrigo.id,
        temperatura: parseFloat(regTemp.temperatura),
        operatore: regTemp.operatore,
        firma_base64,
        note: regTemp.note,
        automatica: false,
        data_specifica
      }, { headers });
      
      if (res.data.alert) {
        toast.error(`⚠️ ATTENZIONE: ${res.data.message}`);
      } else {
        toast.success(`✓ ${selectedFrigo.nome}: ${regTemp.temperatura}°C`);
      }
      
      // Se siamo in modalità compilazione rapida, passa al prossimo
      if (frigoriferiDaCompilare.length > 0 && indiceFrigoCorrente < frigoriferiDaCompilare.length - 1) {
        prossimoFrigorifero();
        loadRegistrazioni(); // Aggiorna calendario
      } else {
        // Fine compilazione
        setShowRegistraTemp(false);
        setDataRegistrazione(null);
        setFrigoriferiDaCompilare([]);
        setIndiceFrigoCorrente(0);
        if (frigoriferiDaCompilare.length > 1) {
          toast.success('Tutti i frigoriferi compilati!');
        }
        loadData();
        loadRegistrazioni();
      }
    } catch (error) {
      console.error('Errore registrazione:', error);
      toast.error('Errore nella registrazione');
    } finally {
      setSaving(false);
    }
  };
  
  // Compilazione massiva - salva tutte le temperature mancanti
  const handleCompilazioneMassiva = async () => {
    if (!massivaOperatore) {
      toast.error('Inserisci il nome operatore');
      return;
    }
    if (massivaFrigoSelezionati.length === 0) {
      toast.error('Seleziona almeno un frigorifero');
      return;
    }
    
    setSavingMassiva(true);
    try {
      const headers = getAuthHeaders();
      
      // Funzione per generare temperatura casuale nel range
      const generaTempCasuale = (tempMin, tempMax) => {
        // Genera un valore casuale con variazione di ±0.5 dal range
        const range = tempMax - tempMin;
        const random = Math.random() * range + tempMin;
        // Arrotonda a 0.5
        return Math.round(random * 2) / 2;
      };
      
      // Calcola i giorni da compilare
      let giorniDaCompilare = [];
      const oggi = new Date();
      oggi.setHours(23, 59, 59, 999);
      
      if (massivaTipo === 'mese') {
        const primoGiorno = new Date(massivaAnno, massivaMese - 1, 1);
        const ultimoGiorno = new Date(massivaAnno, massivaMese, 0);
        
        for (let d = new Date(primoGiorno); d <= ultimoGiorno && d <= oggi; d.setDate(d.getDate() + 1)) {
          giorniDaCompilare.push(new Date(d));
        }
      } else {
        // Range
        const dataDa = new Date(massivaDataDa);
        const dataA = new Date(massivaDataA);
        
        for (let d = new Date(dataDa); d <= dataA && d <= oggi; d.setDate(d.getDate() + 1)) {
          giorniDaCompilare.push(new Date(d));
        }
      }
      
      // Per ogni giorno e ogni frigorifero, crea registrazione con temperatura variabile
      let registrazioniCreate = 0;
      
      for (const giorno of giorniDaCompilare) {
        for (const frigoId of massivaFrigoSelezionati) {
          const frigo = frigoriferi.find(f => f.id === frigoId);
          if (!frigo || isEscluso(frigo)) continue; // spento / in manutenzione: escluso
          const dataConOra = new Date(giorno);
          dataConOra.setHours(12, 0, 0, 0);
          
          // Genera temperatura casuale nel range del frigorifero
          const tempCasuale = generaTempCasuale(frigo.temp_min, frigo.temp_max);
          
          try {
            await axios.post(`${API}/gestione/haccp/frigoriferi/registra-temperatura`, {
              frigorifero_id: frigoId,
              temperatura: tempCasuale,
              operatore: massivaOperatore,
              firma_base64: null,
              note: 'Compilazione massiva',
              automatica: false,
              data_specifica: dataConOra.toISOString()
            }, { headers });
            registrazioniCreate++;
          } catch (err) {
            console.error('Errore registrazione singola:', err);
          }
        }
      }
      
      toast.success(`${registrazioniCreate} registrazioni create!`);
      setShowCompilazioneMassiva(false);
      setMassivaFrigoSelezionati([]);
      setMassivaTemp({});
      setMassivaOperatore('');
      loadData();
      loadRegistrazioni();
      
    } catch (error) {
      console.error('Errore compilazione massiva:', error);
      toast.error('Errore nella compilazione');
    } finally {
      setSavingMassiva(false);
    }
  };

  // Seleziona un giorno nel calendario per vedere le registrazioni
  const selezionaGiorno = (giorno) => {
    setGiornoSelezionato(giorno);
    // Filtra registrazioni per quel giorno
    const regGiorno = registrazioni.filter(r => r.giorno === giorno);
    setRegistrazioniGiorno(regGiorno);
  };

  // Elimina una registrazione
  const eliminaRegistrazione = async (regId) => {
    if (!window.confirm('Eliminare questa registrazione?')) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/gestione/haccp/frigoriferi/registrazioni/${regId}`, { headers });
      toast.success('Registrazione eliminata');
      loadRegistrazioni();
      // Aggiorna anche le registrazioni del giorno selezionato
      if (giornoSelezionato) {
        setRegistrazioniGiorno(prev => prev.filter(r => r.id !== regId));
      }
    } catch (error) {
      console.error('Errore eliminazione:', error);
      toast.error('Errore nell\'eliminazione');
    }
  };

  // Apre modal per modificare una registrazione
  const apriModificaRegistrazione = (reg) => {
    setEditingRegistrazione(reg);
    const frigo = frigoriferi.find(f => f.id === reg.frigorifero_id);
    if (frigo) {
      setSelectedFrigo(frigo);
      setRegTemp({
        temperatura: reg.temperatura.toString(),
        operatore: reg.operatore,
        note: reg.note || '',
        azione_correttiva: reg.azione_correttiva || ''
      });
      setShowRegistraTemp(true);
    }
  };

  // Salva modifica registrazione
  const salvaModificaRegistrazione = async () => {
    if (!regTemp.temperatura || !regTemp.operatore) {
      toast.error('Compila temperatura e nome operatore');
      return;
    }
    
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      
      await axios.put(`${API}/gestione/haccp/frigoriferi/registrazioni/${editingRegistrazione.id}`, {
        temperatura: parseFloat(regTemp.temperatura),
        operatore: regTemp.operatore,
        note: regTemp.note,
        azione_correttiva: regTemp.azione_correttiva
      }, { headers });
      
      toast.success('Registrazione aggiornata');
      setShowRegistraTemp(false);
      setEditingRegistrazione(null);
      loadRegistrazioni();
      if (giornoSelezionato) {
        selezionaGiorno(giornoSelezionato);
      }
    } catch (error) {
      console.error('Errore modifica:', error);
      toast.error('Errore nella modifica');
    } finally {
      setSaving(false);
    }
  };

  // Canvas per firma
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1e40af';
    ctx.lineWidth = 2;
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const openEditFrigo = (frigo) => {
    setEditingFrigo(frigo);
    setNuovoFrigo({
      nome: frigo.nome,
      tipo: frigo.tipo,
      posizione: frigo.posizione || '',
      temp_min: frigo.temp_min,
      temp_max: frigo.temp_max,
      sensore_email: frigo.sensore_email || '',
      note: frigo.note || ''
    });
    setShowAddFrigo(true);
  };

  const mesi = [
    { value: 1, label: 'Gennaio' },
    { value: 2, label: 'Febbraio' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Aprile' },
    { value: 5, label: 'Maggio' },
    { value: 6, label: 'Giugno' },
    { value: 7, label: 'Luglio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Settembre' },
    { value: 10, label: 'Ottobre' },
    { value: 11, label: 'Novembre' },
    { value: 12, label: 'Dicembre' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header con bottone aggiungi */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-full sm:w-auto sm:flex-1">
          <h3 className="text-white lg:text-gray-900 font-semibold">Monitoraggio Temperature</h3>
          <p className="text-xs text-slate-400 lg:text-gray-500">Conforme al Reg. CE 852/2004</p>
        </div>
        <div className="w-full order-last">
          <TemperatureDaScrivere segnale={registrazioni.length} onFuoriRange={apriModificaRegistrazione} onSalvata={() => { loadData(); loadRegistrazioni(); }} />
        </div>
        <button
          onClick={() => {
            setEditingFrigo(null);
            setNuovoFrigo({
              nome: '',
              tipo: 'frigorifero',
              posizione: '',
              temp_min: 0,
              temp_max: 4,
              sensore_email: '',
              note: ''
            });
            setShowAddFrigo(true);
          }}
          className="flex items-center gap-2 px-3 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl text-sm font-medium"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">Aggiungi</span>
        </button>
        <button
          onClick={() => setShowCompilazioneMassiva(true)}
          className="flex items-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-xl text-sm font-medium"
          title="Compilazione massiva"
        >
          <Zap className="h-4 w-4" />
          <span className="hidden sm:inline">Massiva</span>
        </button>
        <button
          onClick={() => {
            // Seleziona tutti i frigoriferi di default
            setFrigoriferiSelezionatiEtichette(frigoriferi.map(f => f.id));
            setShowEtichetteModal(true);
          }}
          className="flex items-center gap-2 px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-medium"
          title="Stampa etichette frigoriferi"
        >
          <Printer className="h-4 w-4" />
          <span>Etichette</span>
        </button>
        <button
          onClick={() => {
            loadSensoriTuya();
            setShowSensoriModal(true);
          }}
          className="flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm font-medium"
          title="Gestione Sensori Ristorantissimo"
        >
          <Wifi className="h-4 w-4" />
          <span className="hidden sm:inline">Sensori</span>
        </button>
        <button
          onClick={() => setShowImpostazioniAuto(true)}
          data-testid="frigoriferi-impostazioni-auto-btn"
          className={`flex items-center gap-2 px-3 py-2 ${impostazioniAuto.abilitato ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-600 hover:bg-slate-700'} text-white rounded-xl text-sm font-medium`}
          title="Impostazioni Compilazione Automatica"
        >
          <Clock className="h-4 w-4" />
          <span className="hidden sm:inline">Auto</span>
        </button>
      </div>

      {/* Stato frigoriferi oggi */}
      {statoOggi.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-slate-400 lg:text-gray-500 font-medium">Stato di oggi</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {statoOggi.map((item) => (
              <div 
                key={item.frigorifero.id}
                className={`p-3 rounded-xl border ${
                  item.registrato_oggi 
                    ? item.ultima_registrazione?.in_range 
                      ? 'bg-green-500/10 border-green-500/30' 
                      : 'bg-red-500/10 border-red-500/30'
                    : 'bg-yellow-500/10 border-yellow-500/30'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Thermometer className={`h-5 w-5 ${
                      item.frigorifero.tipo === 'congelatore' ? 'text-blue-400' : 'text-cyan-400'
                    }`} />
                    <div>
                      <p className="font-medium text-white lg:text-gray-900 text-sm">{item.frigorifero.nome}</p>
                      <p className="text-xs text-slate-400 lg:text-gray-500">
                        {item.frigorifero.tipo === 'congelatore' ? '❄️ Congelatore' : '🧊 Frigorifero'}
                      </p>
                    </div>
                  </div>
                  {item.registrato_oggi ? (
                    <div className="text-right">
                      <p className={`text-lg font-bold ${
                        item.ultima_registrazione?.in_range ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {item.ultima_registrazione?.temperatura}°C
                      </p>
                      <p className="text-xs text-slate-500">{item.ultima_registrazione?.operatore}</p>
                    </div>
                  ) : (
                    <button
                      onClick={() => openRegistraTemp(item.frigorifero)}
                      className="px-3 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg text-xs font-medium"
                    >
                      Registra
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Contatori Frigoriferi e Congelatori */}
      {frigoriferi.length === 0 ? (
        <div className="text-center py-6 bg-black/30 backdrop-blur-sm lg:bg-gray-50 rounded-xl">
          <Thermometer className="h-10 w-10 mx-auto mb-2 text-slate-500" />
          <p className="text-slate-400 lg:text-gray-500 text-sm">Nessun frigorifero configurato</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {/* Contatore Frigoriferi */}
          <button
            data-testid="lista-frigoriferi-btn"
            onClick={() => setShowListaTipo('frigorifero')}
            className="bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 rounded-xl p-3 text-left transition-all active:scale-[0.98]"
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl">🧊</span>
              <span className="text-2xl font-bold text-cyan-400">
                {frigoriferi.filter(f => f.tipo === 'frigorifero').length}
              </span>
            </div>
            <p className="text-xs text-cyan-300/70">Frigoriferi</p>
            {frigoriferi.some(f => f.tipo === 'frigorifero' && isEscluso(f)) && (
              <p data-testid="frigoriferi-non-attivi" className="text-[11px] text-amber-300 mt-0.5">{frigoriferi.filter(f => f.tipo === 'frigorifero' && isEscluso(f)).length} spento/in manutenzione</p>
            )}
          </button>
          
          {/* Contatore Congelatori */}
          <button
            data-testid="lista-congelatori-btn"
            onClick={() => setShowListaTipo('congelatore')}
            className="bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-xl p-3 text-left transition-all active:scale-[0.98]"
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl">❄️</span>
              <span className="text-2xl font-bold text-blue-400">
                {frigoriferi.filter(f => f.tipo === 'congelatore').length}
              </span>
            </div>
            <p className="text-xs text-blue-300/70">Congelatori</p>
            {frigoriferi.some(f => f.tipo === 'congelatore' && isEscluso(f)) && (
              <p data-testid="congelatori-non-attivi" className="text-[11px] text-amber-300 mt-0.5">{frigoriferi.filter(f => f.tipo === 'congelatore' && isEscluso(f)).length} spento/in manutenzione</p>
            )}
          </button>
        </div>
      )}

      {/* Controlli temperature (manuali) */}
      <MonitoraggioTemperature />

      {/* Storico registrazioni */}
      <div className="space-y-3 pt-4 border-t border-white/10 lg:border-gray-200">
        <div className="flex items-center justify-between">
          <p className="text-white lg:text-gray-900 font-semibold">Storico Registrazioni</p>
          <button
            onClick={() => setShowPrintModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-medium"
          >
            <Printer className="h-3.5 w-3.5" />
            Stampa
          </button>
        </div>
        
        {/* Filtri */}
        <div className="flex gap-2 flex-wrap">
          <select
            value={filterAnno}
            onChange={(e) => setFilterAnno(parseInt(e.target.value))}
            className="px-3 py-2 bg-black/40 backdrop-blur-md lg:bg-white border border-white/10 lg:border-gray-200 rounded-lg text-sm text-white lg:text-gray-900"
          >
            {Array.from({ length: new Date().getFullYear() - 2023 }, (_, i) => 2024 + i).map(anno => (
              <option key={anno} value={anno}>{anno}</option>
            ))}
          </select>
          <select
            value={filterMese}
            onChange={(e) => setFilterMese(parseInt(e.target.value))}
            className="px-3 py-2 bg-black/40 backdrop-blur-md lg:bg-white border border-white/10 lg:border-gray-200 rounded-lg text-sm text-white lg:text-gray-900"
          >
            {mesi.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
        
        {/* Calendario con giorni colorati */}
        {(() => {
          const oggi = new Date();
          const primoGiorno = new Date(filterAnno, filterMese - 1, 1);
          const ultimoGiorno = new Date(filterAnno, filterMese, 0);
          const giorniNelMese = ultimoGiorno.getDate();
          const primoGiornoSettimana = primoGiorno.getDay(); // 0 = domenica
          
          // Giorni con registrazioni
          const giorniConRegistrazioni = new Set(
            registrazioni.map(r => r.giorno)
          );
          
          // Genera array di giorni
          const giorni = [];
          // Giorni vuoti all'inizio
          for (let i = 0; i < (primoGiornoSettimana === 0 ? 6 : primoGiornoSettimana - 1); i++) {
            giorni.push(null);
          }
          // Giorni del mese
          for (let giorno = 1; giorno <= giorniNelMese; giorno++) {
            giorni.push(giorno);
          }
          
          return (
            <div className="bg-black/30 lg:bg-gray-50 rounded-xl p-3">
              {/* Header giorni settimana */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['L', 'M', 'M', 'G', 'V', 'S', 'D'].map((g, i) => (
                  <div key={i} className="text-center text-[10px] text-slate-500 font-medium">{g}</div>
                ))}
              </div>
              {/* Griglia giorni */}
              <div className="grid grid-cols-7 gap-1">
                {giorni.map((giorno, idx) => {
                  if (giorno === null) {
                    return <div key={idx} className="h-8" />;
                  }
                  
                  const dataGiorno = new Date(filterAnno, filterMese - 1, giorno);
                  const isOggiOPassato = dataGiorno <= oggi;
                  const haRegistrazione = giorniConRegistrazioni.has(giorno);
                  const isOggi = dataGiorno.toDateString() === oggi.toDateString();
                  const isSelezionato = giornoSelezionato === giorno;
                  
                  let bgColor = 'bg-transparent';
                  let textColor = 'text-slate-600';
                  let extraClass = '';
                  
                  if (isOggiOPassato) {
                    if (haRegistrazione) {
                      bgColor = 'bg-green-500 hover:bg-green-600 cursor-pointer active:scale-95';
                      textColor = 'text-white';
                    } else {
                      bgColor = 'bg-red-500 hover:bg-red-600 cursor-pointer active:scale-95';
                      textColor = 'text-white';
                    }
                  } else {
                    bgColor = 'bg-slate-700/30 lg:bg-gray-200';
                    textColor = 'text-slate-500 lg:text-gray-400';
                  }
                  
                  if (isSelezionato) {
                    extraClass = 'ring-2 ring-yellow-400 ring-offset-2 ring-offset-black/30 scale-110';
                  } else if (isOggi) {
                    extraClass = 'ring-2 ring-white ring-offset-1 ring-offset-transparent';
                  }
                  
                  return (
                    <div
                      key={idx}
                      onClick={() => {
                        if (isOggiOPassato) {
                          if (haRegistrazione) {
                            selezionaGiorno(giorno);
                          } else {
                            openRegistraGiorno(giorno);
                          }
                        }
                      }}
                      className={`h-8 flex items-center justify-center rounded-lg text-xs font-medium transition-all ${bgColor} ${textColor} ${extraClass}`}
                    >
                      {giorno}
                    </div>
                  );
                })}
              </div>
              {/* Legenda */}
              <div className="flex items-center justify-center gap-4 mt-3 pt-2 border-t border-white/10 lg:border-gray-200">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-green-500"></div>
                  <span className="text-[10px] text-slate-400 lg:text-gray-500">Compilato</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-red-500"></div>
                  <span className="text-[10px] text-slate-400 lg:text-gray-500">Mancante</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-slate-700/30 lg:bg-gray-200"></div>
                  <span className="text-[10px] text-slate-400 lg:text-gray-500">Futuro</span>
                </div>
              </div>
            </div>
          );
        })()}
        
        {/* Registrazioni del giorno selezionato */}
        {giornoSelezionato && (
          <div className="bg-black/30 lg:bg-gray-50 rounded-xl p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-white lg:text-gray-900 font-medium text-sm">
                📅 {giornoSelezionato} {mesi.find(m => m.value === filterMese)?.label} {filterAnno}
              </p>
              <button
                onClick={() => { setGiornoSelezionato(null); setRegistrazioniGiorno([]); }}
                className="text-xs text-slate-400 hover:text-white"
              >
                ✕ Chiudi
              </button>
            </div>
            
            {registrazioniGiorno.length === 0 ? (
              <p className="text-slate-500 text-xs text-center py-2">Nessuna registrazione</p>
            ) : (
              <div className="space-y-2">
                {registrazioniGiorno.map((reg) => (
                  <div 
                    key={reg.id}
                    className={`p-2.5 rounded-lg flex items-center justify-between ${
                      reg.stato_operativo === 'non_raggiungibile'
                        ? 'bg-amber-500/15 border border-amber-500/30'
                        : reg.in_range 
                        ? 'bg-green-500/20 border border-green-500/30' 
                        : 'bg-red-500/20 border border-red-500/30'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{reg.frigorifero_tipo === 'congelatore' ? '❄️' : '🧊'}</span>
                      <div>
                        <p className="text-white lg:text-gray-900 text-sm font-medium">{reg.frigorifero_nome}</p>
                        <p className="text-xs text-slate-400">
                          {new Date(reg.data_registrazione).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                          {' • '}{reg.operatore}
                          {reg.lettura_display && ' • letta sul display'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-lg font-bold ${coloreRegistrazione(reg)}`} data-testid={`registrazione-valore-${reg.id}`}>
                        {reg.stato_operativo ? (reg.stato_label || reg.stato_operativo.toUpperCase()) : `${reg.temperatura}°C`}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => apriModificaRegistrazione(reg)}
                          className="p-1.5 bg-blue-500/20 hover:bg-blue-500/40 rounded-lg"
                          title="Modifica"
                        >
                          <Edit className="h-3.5 w-3.5 text-blue-400" />
                        </button>
                        <button
                          onClick={() => eliminaRegistrazione(reg.id)}
                          className="p-1.5 bg-red-500/20 hover:bg-red-500/40 rounded-lg"
                          title="Elimina"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-red-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Pulsante per aggiungere altra registrazione */}
            <button
              onClick={() => {
                const data = new Date(filterAnno, filterMese - 1, giornoSelezionato);
                setDataRegistrazione(data);
                setShowSelezionaFrigo(true);
              }}
              className="w-full py-2 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 rounded-lg text-xs font-medium flex items-center justify-center gap-1"
            >
              <Plus className="h-3.5 w-3.5" />
              Aggiungi registrazione
            </button>
          </div>
        )}
        
        {/* Statistiche periodo */}
        {registrazioni.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-black/30 lg:bg-gray-50 rounded-xl p-2.5 text-center">
              <p className="text-xl font-bold text-white lg:text-gray-900">{registrazioni.length}</p>
              <p className="text-[10px] text-slate-400 lg:text-gray-500">Rilevazioni</p>
            </div>
            <div className="bg-green-500/20 rounded-xl p-2.5 text-center">
              <p className="text-xl font-bold text-green-400">{registrazioni.filter(r => r.in_range && r.stato_operativo !== 'non_raggiungibile').length}</p>
              <p className="text-[10px] text-green-400/70">Conformi</p>
            </div>
            <div className="bg-red-500/20 rounded-xl p-2.5 text-center">
              <p className="text-xl font-bold text-red-400">{registrazioni.filter(r => !r.in_range).length}</p>
              <p className="text-[10px] text-red-400/70">Non Conformi</p>
            </div>
          </div>
        )}
        
        {/* Lista registrazioni del mese (nascosta se c'è giorno selezionato) */}
        {!giornoSelezionato && registrazioni.length === 0 ? (
          <div className="text-center py-4 bg-black/20 lg:bg-gray-50 rounded-xl">
            <p className="text-slate-500 text-sm">Nessuna registrazione per questo periodo</p>
          </div>
        ) : !giornoSelezionato && (
          <div className="space-y-2 max-h-[200px] overflow-y-auto">
            {registrazioni.map((reg) => (
              <div 
                key={reg.id}
                className={`p-3 rounded-xl border ${
                  reg.stato_operativo === 'non_raggiungibile'
                    ? 'bg-amber-500/10 border-amber-500/30'
                    : reg.in_range 
                    ? 'bg-green-500/10 border-green-500/20' 
                    : 'bg-red-500/10 border-red-500/20'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white lg:text-gray-900 text-sm">
                      {reg.frigorifero_nome}
                    </p>
                    <p className="text-xs text-slate-400 lg:text-gray-500">
                      {new Date(reg.data_registrazione).toLocaleString('it-IT')}
                      {reg.automatica && !reg.lettura_display && ' • 🤖 Automatica'}
                      {reg.lettura_display && ' • letta sul display'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold ${coloreRegistrazione(reg)}`} data-testid={`registrazione-valore-mobile-${reg.id}`}>
                      {reg.stato_operativo ? (reg.stato_label || reg.stato_operativo.toUpperCase()) : `${reg.temperatura}°C`}
                    </p>
                    <div className="flex items-center gap-1 justify-end">
                      <p className="text-xs text-slate-500">{reg.operatore}</p>
                      {reg.firma_base64 && <Check className="h-3 w-3 text-green-400" />}
                    </div>
                  </div>
                </div>
                {!reg.in_range && (
                  <p className="mt-2 text-xs text-red-400 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Fuori range ({reg.temp_min}°C - {reg.temp_max}°C)
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal: Aggiungi/Modifica Frigorifero */}
      {showAddFrigo && (
        <div className="fixed inset-0 lg:bg-black/70 flex flex-col lg:items-center lg:justify-center z-[110]">
          <div 
            className="lg:hidden fixed inset-0 bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: 'url(/images/login-bg.png)',
              top: 'calc(-1 * env(safe-area-inset-top, 50px))',
              left: 0,
              right: 0,
              bottom: 'calc(-1 * env(safe-area-inset-bottom, 50px))',
              zIndex: 0,
            }}
          >
            <div className="absolute inset-0 bg-black/60" />
          </div>
          
          <div className="flex-1 lg:flex-none lg:bg-white lg:rounded-2xl w-full lg:max-w-md lg:max-h-[90vh] overflow-hidden flex flex-col relative z-10">
            <div className="p-4 border-b border-white/10 lg:border-gray-100 flex items-center justify-between bg-transparent lg:bg-gradient-to-r lg:from-cyan-50 lg:to-blue-50 flex-shrink-0" style={{ paddingTop: 'calc(env(safe-area-inset-top, 44px) + 16px)' }}>
              <div className="flex items-center gap-3">
                <button onClick={() => { setShowAddFrigo(false); setEditingFrigo(null); }} className="p-2 bg-black/30 lg:bg-white/50 rounded-xl">
                  <ChevronLeft className="h-5 w-5 text-white lg:text-gray-600" />
                </button>
                <h3 className="font-bold text-white lg:text-gray-900">
                  {editingFrigo ? 'Modifica Frigorifero' : 'Nuovo Frigorifero'}
                </h3>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-24 lg:pb-4">
              <div>
                <label className="block text-sm font-medium text-white lg:text-gray-700 mb-1">Nome *</label>
                <input
                  type="text"
                  value={nuovoFrigo.nome}
                  onChange={(e) => setNuovoFrigo({...nuovoFrigo, nome: e.target.value})}
                  placeholder="Es: Frigo Cucina 1"
                  className="w-full px-3 py-2.5 bg-black/40 lg:bg-white border border-white/20 lg:border-gray-200 rounded-xl text-white lg:text-gray-900 placeholder-slate-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white lg:text-gray-700 mb-1">Tipo *</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setNuovoFrigo({...nuovoFrigo, tipo: 'frigorifero', temp_min: 0, temp_max: 4})}
                    className={`p-3 rounded-xl border-2 flex flex-col items-center ${
                      nuovoFrigo.tipo === 'frigorifero' 
                        ? 'border-cyan-500 bg-cyan-500/20' 
                        : 'border-white/20 lg:border-gray-200'
                    }`}
                  >
                    <span className="text-2xl mb-1">🧊</span>
                    <span className="text-white lg:text-gray-900 font-medium text-sm">Frigorifero</span>
                    <span className="text-xs text-slate-400 lg:text-gray-500">0°C / +10°C</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setNuovoFrigo({...nuovoFrigo, tipo: 'congelatore', temp_min: -24, temp_max: -18})}
                    className={`p-3 rounded-xl border-2 flex flex-col items-center ${
                      nuovoFrigo.tipo === 'congelatore' 
                        ? 'border-blue-500 bg-blue-500/20' 
                        : 'border-white/20 lg:border-gray-200'
                    }`}
                  >
                    <span className="text-2xl mb-1">❄️</span>
                    <span className="text-white lg:text-gray-900 font-medium text-sm">Congelatore</span>
                    <span className="text-xs text-slate-400 lg:text-gray-500">-24°C / -18°C</span>
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-white lg:text-gray-700 mb-1">Temp. Min (°C)</label>
                  <input
                    type="number"
                    value={nuovoFrigo.temp_min}
                    onChange={(e) => setNuovoFrigo({...nuovoFrigo, temp_min: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2.5 bg-black/40 lg:bg-white border border-white/20 lg:border-gray-200 rounded-xl text-white lg:text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white lg:text-gray-700 mb-1">Temp. Max (°C)</label>
                  <input
                    type="number"
                    value={nuovoFrigo.temp_max}
                    onChange={(e) => setNuovoFrigo({...nuovoFrigo, temp_max: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2.5 bg-black/40 lg:bg-white border border-white/20 lg:border-gray-200 rounded-xl text-white lg:text-gray-900"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white lg:text-gray-700 mb-1">Posizione</label>
                <input
                  type="text"
                  value={nuovoFrigo.posizione}
                  onChange={(e) => setNuovoFrigo({...nuovoFrigo, posizione: e.target.value})}
                  placeholder="Es: Cucina, Magazzino"
                  className="w-full px-3 py-2.5 bg-black/40 lg:bg-white border border-white/20 lg:border-gray-200 rounded-xl text-white lg:text-gray-900 placeholder-slate-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white lg:text-gray-700 mb-1">Email Sensore (opzionale)</label>
                <input
                  type="email"
                  value={nuovoFrigo.sensore_email}
                  onChange={(e) => setNuovoFrigo({...nuovoFrigo, sensore_email: e.target.value})}
                  placeholder="sensore@esempio.com"
                  className="w-full px-3 py-2.5 bg-black/40 lg:bg-white border border-white/20 lg:border-gray-200 rounded-xl text-white lg:text-gray-900 placeholder-slate-500"
                />
                <p className="text-xs text-slate-500 mt-1">Per rilevazione automatica da sensori</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white lg:text-gray-700 mb-1">Note</label>
                <textarea
                  value={nuovoFrigo.note}
                  onChange={(e) => setNuovoFrigo({...nuovoFrigo, note: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2.5 bg-black/40 lg:bg-white border border-white/20 lg:border-gray-200 rounded-xl text-white lg:text-gray-900 placeholder-slate-500"
                />
              </div>
            </div>
            
            <div className="fixed lg:relative lg:bottom-0 safe-footer left-0 right-0 p-4 border-t border-white/10 lg:border-gray-100 bg-black/60 backdrop-blur-md lg:bg-white flex gap-2 z-[111]">
              <button
                onClick={() => { setShowAddFrigo(false); setEditingFrigo(null); }}
                className="flex-1 py-3 bg-black/40 lg:bg-gray-100 text-white lg:text-gray-700 rounded-xl font-medium"
              >
                Annulla
              </button>
              <button
                onClick={handleAddFrigo}
                disabled={saving}
                className="flex-1 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl font-medium flex items-center justify-center gap-2"
              >
                {saving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <Check className="h-4 w-4" />}
                {editingFrigo ? 'Aggiorna' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Registra Temperatura */}
      {showRegistraTemp && selectedFrigo && (
        <div className="fixed inset-0 lg:bg-black/70 flex flex-col lg:items-center lg:justify-center z-[110]">
          <div 
            className="lg:hidden fixed inset-0 bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: 'url(/images/login-bg.png)',
              top: 'calc(-1 * env(safe-area-inset-top, 50px))',
              left: 0,
              right: 0,
              bottom: 'calc(-1 * env(safe-area-inset-bottom, 50px))',
              zIndex: 0,
            }}
          >
            <div className="absolute inset-0 bg-black/60" />
          </div>
          
          <div className="flex-1 lg:flex-none lg:bg-white lg:rounded-2xl w-full lg:max-w-md lg:max-h-[90vh] overflow-hidden flex flex-col relative z-10">
            <div className="p-4 border-b border-white/10 lg:border-gray-100 flex items-center justify-between bg-transparent lg:bg-gradient-to-r lg:from-green-50 lg:to-emerald-50 flex-shrink-0" style={{ paddingTop: 'calc(env(safe-area-inset-top, 44px) + 16px)' }}>
              <div className="flex items-center gap-3">
                <button onClick={() => { setShowRegistraTemp(false); setDataRegistrazione(null); }} className="p-2 bg-black/30 lg:bg-white/50 rounded-xl">
                  <ChevronLeft className="h-5 w-5 text-white lg:text-gray-600" />
                </button>
                <div>
                  <h3 className="font-bold text-white lg:text-gray-900">Registra Temperatura</h3>
                  <p className="text-xs text-green-400 lg:text-green-600">{selectedFrigo.nome}</p>
                </div>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-24 lg:pb-4">
              {/* Banner data specifica */}
              {dataRegistrazione && (
                <div className="p-3 bg-orange-500/20 border border-orange-500/30 rounded-xl flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-orange-400" />
                  <div>
                    <p className="text-orange-400 text-sm font-medium">Registrazione retroattiva</p>
                    <p className="text-orange-300 text-xs">
                      {dataRegistrazione.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Progresso compilazione sequenziale */}
              {frigoriferiDaCompilare.length > 1 && (
                <div className="p-3 bg-blue-500/20 border border-blue-500/30 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-blue-400 text-sm font-medium">Compilazione rapida</span>
                    <span className="text-blue-300 text-xs">{indiceFrigoCorrente + 1} / {frigoriferiDaCompilare.length}</span>
                  </div>
                  <div className="w-full bg-blue-900/30 rounded-full h-1.5">
                    <div 
                      className="bg-blue-500 h-1.5 rounded-full transition-all" 
                      style={{ width: `${((indiceFrigoCorrente + 1) / frigoriferiDaCompilare.length) * 100}%` }}
                    />
                  </div>
                </div>
              )}
              
              {/* Info frigorifero */}
              <div className="p-3 bg-black/30 lg:bg-gray-50 rounded-xl">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400 lg:text-gray-500 text-sm">Range accettabile:</span>
                  <span className="text-white lg:text-gray-900 font-medium">
                    {selectedFrigo.temp_min}°C / {selectedFrigo.temp_max}°C
                  </span>
                </div>
              </div>
              
              {/* Selettore Temperatura con Wheel/Slider */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-white lg:text-gray-700 text-center">Temperatura rilevata</label>
                
                {/* Display temperatura grande */}
                <div className={`p-6 rounded-2xl text-center ${
                  regTemp.temperatura !== '' && 
                  parseFloat(regTemp.temperatura) >= selectedFrigo.temp_min && 
                  parseFloat(regTemp.temperatura) <= selectedFrigo.temp_max
                    ? 'bg-green-500/20 border-2 border-green-500/50'
                    : regTemp.temperatura !== ''
                    ? 'bg-red-500/20 border-2 border-red-500/50'
                    : 'bg-black/30 border-2 border-white/20'
                }`}>
                  <div className="flex items-center justify-center gap-4">
                    {/* Bottone - */}
                    <button
                      type="button"
                      onClick={() => {
                        const current = parseFloat(regTemp.temperatura) || (selectedFrigo.tipo === 'congelatore' ? -20 : 2);
                        setRegTemp({...regTemp, temperatura: (current - 0.5).toFixed(1)});
                      }}
                      className="w-14 h-14 rounded-full bg-blue-500/30 hover:bg-blue-500/50 flex items-center justify-center text-white text-2xl font-bold active:scale-95 transition-transform"
                    >
                      −
                    </button>
                    
                    {/* Temperatura */}
                    <div className="flex-1">
                      <span className={`text-5xl font-bold ${
                        regTemp.temperatura !== '' && 
                        parseFloat(regTemp.temperatura) >= selectedFrigo.temp_min && 
                        parseFloat(regTemp.temperatura) <= selectedFrigo.temp_max
                          ? 'text-green-400'
                          : regTemp.temperatura !== ''
                          ? 'text-red-400'
                          : 'text-white'
                      }`}>
                        {regTemp.temperatura !== '' ? regTemp.temperatura : '--'}
                      </span>
                      <span className="text-2xl text-slate-400 ml-1">°C</span>
                    </div>
                    
                    {/* Bottone + */}
                    <button
                      type="button"
                      onClick={() => {
                        const current = parseFloat(regTemp.temperatura) || (selectedFrigo.tipo === 'congelatore' ? -20 : 2);
                        setRegTemp({...regTemp, temperatura: (current + 0.5).toFixed(1)});
                      }}
                      className="w-14 h-14 rounded-full bg-red-500/30 hover:bg-red-500/50 flex items-center justify-center text-white text-2xl font-bold active:scale-95 transition-transform"
                    >
                      +
                    </button>
                  </div>
                  
                  {/* Indicatore stato */}
                  {regTemp.temperatura !== '' && (
                    <p className={`mt-2 text-sm font-medium ${
                      parseFloat(regTemp.temperatura) >= selectedFrigo.temp_min && 
                      parseFloat(regTemp.temperatura) <= selectedFrigo.temp_max
                        ? 'text-green-400'
                        : 'text-red-400'
                    }`}>
                      {parseFloat(regTemp.temperatura) >= selectedFrigo.temp_min && 
                       parseFloat(regTemp.temperatura) <= selectedFrigo.temp_max
                        ? '✓ Temperatura OK'
                        : '⚠ Fuori range!'}
                    </p>
                  )}
                </div>
                
                {/* Slider */}
                <div className="px-2">
                  <input
                    type="range"
                    min={selectedFrigo.tipo === 'congelatore' ? -30 : -5}
                    max={selectedFrigo.tipo === 'congelatore' ? 0 : 15}
                    step="0.5"
                    value={regTemp.temperatura || (selectedFrigo.tipo === 'congelatore' ? -20 : 2)}
                    onChange={(e) => setRegTemp({...regTemp, temperatura: e.target.value})}
                    className="w-full h-3 rounded-full appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, 
                        #3b82f6 0%, 
                        #22c55e ${((selectedFrigo.temp_min - (selectedFrigo.tipo === 'congelatore' ? -30 : -5)) / (selectedFrigo.tipo === 'congelatore' ? 30 : 20)) * 100}%, 
                        #22c55e ${((selectedFrigo.temp_max - (selectedFrigo.tipo === 'congelatore' ? -30 : -5)) / (selectedFrigo.tipo === 'congelatore' ? 30 : 20)) * 100}%, 
                        #ef4444 100%)`
                    }}
                  />
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>{selectedFrigo.tipo === 'congelatore' ? '-30°C' : '-5°C'}</span>
                    <span className="text-green-400">{selectedFrigo.temp_min}°C</span>
                    <span className="text-green-400">{selectedFrigo.temp_max}°C</span>
                    <span>{selectedFrigo.tipo === 'congelatore' ? '0°C' : '+15°C'}</span>
                  </div>
                </div>
                
                {/* Quick buttons per valori comuni */}
                <div className="flex flex-wrap gap-2 justify-center">
                  {(selectedFrigo.tipo === 'congelatore' 
                    ? [-24, -22, -20, -18, -16]
                    : [0, 1, 2, 3, 4, 5, 6]
                  ).map(temp => (
                    <button
                      key={temp}
                      type="button"
                      onClick={() => setRegTemp({...regTemp, temperatura: temp.toString()})}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        parseFloat(regTemp.temperatura) === temp
                          ? 'bg-cyan-500 text-white'
                          : 'bg-black/30 lg:bg-gray-100 text-slate-300 lg:text-gray-600 hover:bg-black/50 lg:hover:bg-gray-200'
                      }`}
                    >
                      {temp}°C
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white lg:text-gray-700 mb-1">Nome Operatore *</label>
                <input
                  type="text"
                  value={regTemp.operatore}
                  onChange={(e) => setRegTemp({...regTemp, operatore: e.target.value})}
                  placeholder="Es: Mario Rossi"
                  className="w-full px-3 py-2.5 bg-black/40 lg:bg-white border border-white/20 lg:border-gray-200 rounded-xl text-white lg:text-gray-900 placeholder-slate-500"
                />
              </div>
              
              {/* Area firma */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-white lg:text-gray-700">Firma Operatore</label>
                  <button onClick={clearSignature} className="text-xs text-red-400 hover:text-red-300">Cancella</button>
                </div>
                <canvas
                  ref={canvasRef}
                  width={300}
                  height={100}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  className="w-full h-24 bg-white rounded-xl border-2 border-dashed border-slate-400 cursor-crosshair touch-none"
                  style={{ touchAction: 'none' }}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white lg:text-gray-700 mb-1">Note (opzionale)</label>
                <textarea
                  value={regTemp.note}
                  onChange={(e) => setRegTemp({...regTemp, note: e.target.value})}
                  rows={2}
                  placeholder="Eventuali anomalie o osservazioni..."
                  className="w-full px-3 py-2.5 bg-black/40 lg:bg-white border border-white/20 lg:border-gray-200 rounded-xl text-white lg:text-gray-900 placeholder-slate-500"
                />
              </div>
              
              {editingRegistrazione && editingRegistrazione.anomalia && (
                <div data-testid="azione-correttiva-box" className="p-3 rounded-xl border border-red-400/40 bg-red-500/10">
                  <label className="block text-sm font-medium text-red-200 lg:text-red-800 mb-1">⚠️ Non conformità: azione correttiva adottata</label>
                  <textarea
                    data-testid="azione-correttiva-input"
                    value={regTemp.azione_correttiva}
                    onChange={(e) => setRegTemp({...regTemp, azione_correttiva: e.target.value})}
                    rows={2}
                    placeholder="Es. prodotti spostati in altro frigo, chiamato tecnico, temperatura rientrata alle 15:30..."
                    className="w-full px-3 py-2.5 bg-black/40 lg:bg-white border border-red-300/40 lg:border-red-200 rounded-xl text-white lg:text-gray-900 placeholder-slate-500"
                  />
                  <p className="text-[11px] text-red-200/80 lg:text-red-700/80 mt-1">Viene stampata nel Registro Anomalie Temperature.</p>
                </div>
              )}
            </div>
            
            <div className="fixed lg:relative lg:bottom-0 safe-footer left-0 right-0 p-4 border-t border-white/10 lg:border-gray-100 bg-black/60 backdrop-blur-md lg:bg-white flex gap-2 z-[111]">
              <button
                onClick={() => { setShowRegistraTemp(false); setEditingRegistrazione(null); }}
                className="flex-1 py-3 bg-black/40 lg:bg-gray-100 text-white lg:text-gray-700 rounded-xl font-medium"
              >
                Annulla
              </button>
              <button
                data-testid="registra-temp-salva-btn"
                onClick={editingRegistrazione ? salvaModificaRegistrazione : handleRegistraTemp}
                disabled={saving}
                className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium flex items-center justify-center gap-2"
              >
                {saving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <Check className="h-4 w-4" />}
                {editingRegistrazione ? 'Salva modifiche' : 'Registra'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Seleziona Frigorifero per giorno specifico */}
      {showSelezionaFrigo && dataRegistrazione && (
        <div className="fixed inset-0 bg-black/70 flex items-end lg:items-center justify-center z-[120]">
          <div className="bg-white rounded-t-2xl lg:rounded-2xl w-full lg:max-w-md max-h-[70vh] overflow-hidden shadow-2xl">
            <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-red-50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-gray-900">Compila giorno mancante</h3>
                  <p className="text-sm text-orange-600">
                    {dataRegistrazione.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
                <button onClick={() => { setShowSelezionaFrigo(false); setDataRegistrazione(null); setFrigoriferiDaCompilare([]); }} className="p-1.5 hover:bg-white/50 rounded-lg">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>
            
            <div className="p-4">
              {/* Pulsante Compila Tutti */}
              {frigoriferi.length > 1 && (
                <button
                  onClick={startCompilazioneRapida}
                  className="w-full mb-3 flex items-center justify-center gap-2 p-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-xl font-medium transition-all"
                >
                  <Zap className="h-5 w-5" />
                  Compila tutti ({frigoriferi.length} frigoriferi)
                </button>
              )}
              
              <p className="text-sm text-gray-600 mb-3">Oppure seleziona singolarmente:</p>
              <div className="space-y-2">
                {frigoriferi.map((frigo) => (
                  <button
                    key={frigo.id}
                    onClick={() => {
                      setShowSelezionaFrigo(false);
                      setFrigoriferiDaCompilare([frigo]); // Solo questo frigo
                      setIndiceFrigoCorrente(0);
                      openRegistraTemp(frigo, dataRegistrazione);
                    }}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{frigo.tipo === 'congelatore' ? '❄️' : '🧊'}</span>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">{frigo.nome}</p>
                        <p className="text-xs text-gray-500">{frigo.temp_min}°C / {frigo.temp_max}°C</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </button>
                ))}
              </div>
            </div>
            
            <div className="p-4 border-t border-gray-100">
              <button
                onClick={() => { setShowSelezionaFrigo(false); setDataRegistrazione(null); setFrigoriferiDaCompilare([]); }}
                className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium text-sm"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Elenco Frigoriferi/Congelatori */}
      {showListaTipo && (
        <div className="fixed inset-0 bg-black/70 flex items-end lg:items-center justify-center z-[120]">
          <div className="bg-white rounded-t-2xl lg:rounded-2xl w-full lg:max-w-md max-h-[70vh] overflow-hidden shadow-2xl">
            <div className={`p-4 border-b border-gray-100 flex items-center justify-between ${
              showListaTipo === 'congelatore' 
                ? 'bg-gradient-to-r from-blue-50 to-indigo-50' 
                : 'bg-gradient-to-r from-cyan-50 to-teal-50'
            }`}>
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <span className="text-xl">{showListaTipo === 'congelatore' ? '❄️' : '🧊'}</span>
                {showListaTipo === 'congelatore' ? 'Congelatori' : 'Frigoriferi'}
              </h3>
              <button onClick={() => setShowListaTipo(null)} className="p-1.5 hover:bg-white/50 rounded-lg">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[50vh]">
              {frigoriferi.filter(f => f.tipo === showListaTipo).length === 0 ? (
                <p className="text-center text-gray-500 py-4">Nessun {showListaTipo} configurato</p>
              ) : (
                <div className="space-y-2">
                  {frigoriferi.filter(f => f.tipo === showListaTipo).map((frigo) => (
                    <div 
                      key={frigo.id}
                      data-testid={`frigo-riga-${frigo.id}`}
                      className={`flex items-center justify-between p-3 rounded-xl ${isEscluso(frigo) ? 'bg-gray-100 opacity-80' : 'bg-gray-50'}`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-gray-900">{frigo.nome}</p>
                          <StatoFrigoBadge frigo={frigo} />
                        </div>
                        <p className="text-xs text-gray-500">
                          Range: {frigo.temp_min}°C / {frigo.temp_max}°C
                          {frigo.posizione && ` • ${frigo.posizione}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          data-testid={`frigo-stato-btn-${frigo.id}`}
                          onClick={() => apriStatoFrigo(frigo)}
                          className={`p-2 rounded-lg ${isEscluso(frigo) ? 'bg-amber-100 hover:bg-amber-200' : 'bg-gray-100 hover:bg-gray-200'}`}
                          title="Stato: attivo / spento / manutenzione"
                        >
                          <Power className={`h-4 w-4 ${isEscluso(frigo) ? 'text-amber-600' : 'text-gray-500'}`} />
                        </button>
                        <button
                          data-testid={`frigo-registra-btn-${frigo.id}`}
                          onClick={() => { setShowListaTipo(null); openRegistraTemp(frigo); }}
                          className={`p-2 rounded-lg ${isEscluso(frigo) ? 'bg-gray-100 cursor-not-allowed' : 'bg-green-100 hover:bg-green-200'}`}
                          title={isEscluso(frigo) ? 'Frigo non attivo' : 'Registra temperatura'}
                        >
                          <Plus className={`h-4 w-4 ${isEscluso(frigo) ? 'text-gray-400' : 'text-green-600'}`} />
                        </button>
                        <button
                          onClick={() => { setShowListaTipo(null); openEditFrigo(frigo); }}
                          className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                          title="Modifica"
                        >
                          <Edit className="h-4 w-4 text-gray-500" />
                        </button>
                        <button
                          onClick={() => {
                            const tenantDb = localStorage.getItem('tenant_db');
                            const url = `${API}/gestione/haccp/frigoriferi/etichetta-pdf/${frigo.id}?tenant=${tenantDb}`;
                            window.open(url, '_blank');
                          }}
                          className="p-2 bg-cyan-100 hover:bg-cyan-200 rounded-lg"
                          title="Stampa Etichetta"
                        >
                          <Printer className="h-4 w-4 text-cyan-600" />
                        </button>
                        <button
                          onClick={() => handleDeleteFrigo(frigo.id)}
                          className="p-2 bg-red-100 hover:bg-red-200 rounded-lg"
                          title="Elimina"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-gray-100">
              <button
                onClick={() => setShowListaTipo(null)}
                className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium text-sm"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Stato operativo frigorifero (attivo / spento / manutenzione) */}
      {statoFrigoModal && (
        <StatoFrigoModal
          frigo={statoFrigoModal}
          form={statoForm}
          setForm={setStatoForm}
          onSave={salvaStatoFrigo}
          onClose={() => setStatoFrigoModal(null)}
          saving={salvandoStato}
        />
      )}

      {/* Modal: Compilazione Massiva */}
      {showCompilazioneMassiva && (
        <div className="fixed inset-0 lg:bg-black/70 flex flex-col lg:items-center lg:justify-center z-[130]">
          {/* Mobile Background */}
          <div 
            className="lg:hidden fixed inset-0 bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: 'url(/images/login-bg.png)',
              top: 'calc(-1 * env(safe-area-inset-top, 50px))',
              zIndex: 0,
            }}
          >
            <div className="absolute inset-0 bg-black/60" />
          </div>
          
          <div className="flex-1 lg:flex-none lg:bg-white lg:rounded-2xl w-full lg:max-w-md lg:max-h-[90vh] overflow-hidden flex flex-col relative z-10">
            <div className="p-4 border-b border-white/10 lg:border-gray-100 flex items-center justify-between bg-transparent lg:bg-gradient-to-r lg:from-orange-50 lg:to-red-50 flex-shrink-0" style={{ paddingTop: 'calc(env(safe-area-inset-top, 44px) + 16px)' }}>
              <div className="flex items-center gap-3">
                <button onClick={() => setShowCompilazioneMassiva(false)} className="p-2 bg-black/30 lg:bg-white/50 rounded-xl">
                  <ChevronLeft className="h-5 w-5 text-white lg:text-gray-600" />
                </button>
                <div>
                  <h3 className="font-bold text-white lg:text-gray-900">Compilazione Massiva</h3>
                  <p className="text-xs text-orange-400 lg:text-orange-600">Per controlli o dimenticanze</p>
                </div>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-24 lg:pb-4">
              {/* Tipo periodo */}
              <div>
                <label className="block text-sm font-medium text-white lg:text-gray-700 mb-2">Periodo da compilare</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setMassivaTipo('mese')}
                    className={`py-2.5 px-4 rounded-xl text-sm font-medium transition-all ${
                      massivaTipo === 'mese' 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-black/30 lg:bg-gray-100 text-white lg:text-gray-600'
                    }`}
                  >
                    Per Mese
                  </button>
                  <button
                    onClick={() => setMassivaTipo('range')}
                    className={`py-2.5 px-4 rounded-xl text-sm font-medium transition-all ${
                      massivaTipo === 'range' 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-black/30 lg:bg-gray-100 text-white lg:text-gray-600'
                    }`}
                  >
                    Range Date
                  </button>
                </div>
              </div>
              
              {/* Selettori periodo */}
              {massivaTipo === 'mese' ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-400 lg:text-gray-500 mb-1">Mese</label>
                    <select
                      value={massivaMese}
                      onChange={(e) => setMassivaMese(parseInt(e.target.value))}
                      className="w-full px-3 py-2.5 bg-black/40 lg:bg-white border border-white/20 lg:border-gray-200 rounded-xl text-white lg:text-gray-900"
                    >
                      {mesi.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 lg:text-gray-500 mb-1">Anno</label>
                    <select
                      value={massivaAnno}
                      onChange={(e) => setMassivaAnno(parseInt(e.target.value))}
                      className="w-full px-3 py-2.5 bg-black/40 lg:bg-white border border-white/20 lg:border-gray-200 rounded-xl text-white lg:text-gray-900"
                    >
                      {Array.from({ length: new Date().getFullYear() - 2023 }, (_, i) => 2024 + i).map(anno => (
                        <option key={anno} value={anno}>{anno}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-400 lg:text-gray-500 mb-1">Da</label>
                    <input
                      type="date"
                      value={massivaDataDa}
                      onChange={(e) => setMassivaDataDa(e.target.value)}
                      className="w-full px-3 py-2.5 bg-black/40 lg:bg-white border border-white/20 lg:border-gray-200 rounded-xl text-white lg:text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 lg:text-gray-500 mb-1">A</label>
                    <input
                      type="date"
                      value={massivaDataA}
                      onChange={(e) => setMassivaDataA(e.target.value)}
                      className="w-full px-3 py-2.5 bg-black/40 lg:bg-white border border-white/20 lg:border-gray-200 rounded-xl text-white lg:text-gray-900"
                    />
                  </div>
                </div>
              )}
              
              {/* Selezione frigoriferi */}
              <div>
                <label className="block text-sm font-medium text-white lg:text-gray-700 mb-2">Seleziona frigoriferi</label>
                <div className="space-y-2">
                  {frigoriferi.map((frigo) => (
                    <label
                      key={frigo.id}
                      className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                        massivaFrigoSelezionati.includes(frigo.id)
                          ? 'bg-orange-500/20 border-2 border-orange-500'
                          : 'bg-black/30 lg:bg-gray-50 border-2 border-transparent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={massivaFrigoSelezionati.includes(frigo.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setMassivaFrigoSelezionati([...massivaFrigoSelezionati, frigo.id]);
                          } else {
                            setMassivaFrigoSelezionati(massivaFrigoSelezionati.filter(id => id !== frigo.id));
                          }
                        }}
                        className="w-5 h-5 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                      />
                      <span className="text-xl">{frigo.tipo === 'congelatore' ? '❄️' : '🧊'}</span>
                      <div className="flex-1">
                        <p className="font-medium text-white lg:text-gray-900">{frigo.nome}</p>
                        <p className="text-xs text-slate-400 lg:text-gray-500">
                          Range: {frigo.temp_min}°C → {frigo.temp_max}°C
                        </p>
                      </div>
                      {massivaFrigoSelezionati.includes(frigo.id) && (
                        <Check className="h-5 w-5 text-orange-500" />
                      )}
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Nome operatore */}
              <div>
                <label className="block text-sm font-medium text-white lg:text-gray-700 mb-1">Nome Operatore</label>
                <input
                  type="text"
                  value={massivaOperatore}
                  onChange={(e) => setMassivaOperatore(e.target.value)}
                  placeholder="Es: Mario Rossi"
                  className="w-full px-3 py-2.5 bg-black/40 lg:bg-white border border-white/20 lg:border-gray-200 rounded-xl text-white lg:text-gray-900 placeholder-slate-500"
                />
              </div>
              
              {/* Avviso */}
              <div className="p-3 bg-green-500/20 border border-green-500/30 rounded-xl">
                <p className="text-green-400 text-xs">
                  ✓ Le temperature verranno generate automaticamente nel range consentito di ogni frigorifero, 
                  con variazioni realistiche giorno per giorno.
                </p>
              </div>
              <div className="p-3 bg-yellow-500/20 border border-yellow-500/30 rounded-xl">
                <p className="text-yellow-400 text-xs">
                  ⚠️ Verranno create registrazioni per tutti i giorni del periodo (fino ad oggi). 
                  I giorni già compilati non verranno sovrascritti.
                </p>
              </div>
            </div>
            
            <div className="fixed lg:relative lg:bottom-0 safe-footer left-0 right-0 p-4 border-t border-white/10 lg:border-gray-100 bg-black/60 backdrop-blur-md lg:bg-white flex gap-2 z-[131]">
              <button
                onClick={() => setShowCompilazioneMassiva(false)}
                className="flex-1 py-3 bg-black/40 lg:bg-gray-100 text-white lg:text-gray-700 rounded-xl font-medium"
              >
                Annulla
              </button>
              <button
                onClick={handleCompilazioneMassiva}
                disabled={savingMassiva || massivaFrigoSelezionati.length === 0}
                className="flex-1 py-3 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white rounded-xl font-medium flex items-center justify-center gap-2"
              >
                {savingMassiva ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <Zap className="h-4 w-4" />
                )}
                Compila Tutto
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Opzioni Stampa */}
      {showPrintModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[120] p-4">
          <div className="bg-white lg:bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-purple-50 to-indigo-50">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <Printer className="h-5 w-5 text-purple-600" />
                Stampa Report
              </h3>
              <button onClick={() => setShowPrintModal(false)} className="p-1.5 hover:bg-white/50 rounded-lg">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Tipo periodo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Periodo</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setPrintType('mese')}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      printType === 'mese' 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Mese
                  </button>
                  <button
                    onClick={() => setPrintType('anno')}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      printType === 'anno' 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Anno
                  </button>
                  <button
                    onClick={() => setPrintType('range')}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      printType === 'range' 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Range
                  </button>
                </div>
              </div>
              
              {/* Selettori in base al tipo */}
              {printType === 'mese' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Mese</label>
                    <select
                      value={printMese}
                      onChange={(e) => setPrintMese(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    >
                      {mesi.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Anno</label>
                    <select
                      value={printAnno}
                      onChange={(e) => setPrintAnno(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    >
                      {Array.from({ length: new Date().getFullYear() - 2023 }, (_, i) => 2024 + i).map(anno => (
                        <option key={anno} value={anno}>{anno}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              
              {printType === 'anno' && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Anno</label>
                  <select
                    value={printAnno}
                    onChange={(e) => setPrintAnno(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  >
                    {Array.from({ length: new Date().getFullYear() - 2023 }, (_, i) => 2024 + i).map(anno => (
                      <option key={anno} value={anno}>{anno}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {printType === 'range' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Da</label>
                    <input
                      type="date"
                      value={printDataDa}
                      onChange={(e) => setPrintDataDa(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">A</label>
                    <input
                      type="date"
                      value={printDataA}
                      onChange={(e) => setPrintDataA(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-gray-100 flex flex-col gap-2">
              <div className="flex gap-2">
              <button
                onClick={() => setShowPrintModal(false)}
                className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium text-sm"
              >
                Annulla
              </button>
              <button
                data-testid="report-temperature-pdf-btn"
                onClick={() => {
                  if (printType === 'range' && (!printDataDa || !printDataA)) {
                    toast.error('Seleziona entrambe le date');
                    return;
                  }
                  // Apertura DIRETTA e sincrona: unico metodo affidabile anche su iPhone/PWA
                  // (stesso pattern delle etichette PDF che funzionano)
                  window.open(`${API}/gestione/haccp/frigoriferi/report-pdf?${buildPrintParams().toString()}`, '_blank');
                  toast.success('Report aperto');
                  setShowPrintModal(false);
                }}
                disabled={printing || (printType === 'range' && (!printDataDa || !printDataA))}
                className="flex-1 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-xl font-medium text-sm flex items-center justify-center gap-2"
              >
                {printing ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Registro Temperature
              </button>
              </div>
              <button
                data-testid="report-anomalie-pdf-btn"
                onClick={() => {
                  if (printType === 'range' && (!printDataDa || !printDataA)) {
                    toast.error('Seleziona entrambe le date');
                    return;
                  }
                  window.open(`${API}/gestione/haccp/frigoriferi/registro-anomalie-pdf?${buildPrintParams().toString()}`, '_blank');
                  toast.success('Registro anomalie aperto');
                  setShowPrintModal(false);
                }}
                disabled={printType === 'range' && (!printDataDa || !printDataA)}
                className="w-full py-2.5 bg-red-700 hover:bg-red-800 disabled:bg-red-400 text-white rounded-xl font-medium text-sm flex items-center justify-center gap-2"
              >
                <AlertTriangle className="h-4 w-4" />
                Registro Anomalie (PDF separato)
              </button>
              <p className="text-[11px] text-gray-500 text-center">Il registro temperature contiene un QR code che apre il registro anomalie dello stesso periodo.</p>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Gestione Sensori Ristorantissimo */}
      {showSensoriModal && (
        <div className="fixed inset-0 bg-black/70 flex items-end lg:items-center justify-center z-[130]">
          <div className="bg-slate-900/95 backdrop-blur-sm lg:bg-white rounded-t-3xl lg:rounded-2xl w-full lg:max-w-xl max-h-[90vh] lg:max-h-[80vh] flex flex-col mb-16 lg:mb-0">
            {/* Header */}
            <div className="p-4 border-b border-slate-700 lg:border-gray-100 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <Wifi className="h-5 w-5 text-purple-400 lg:text-purple-600" />
                <div>
                  <h3 className="font-bold text-white lg:text-gray-900">Sensori Ristorantissimo</h3>
                  <p className="text-xs text-slate-400 lg:text-gray-500">
                    {abbinandoFrigo ? `Seleziona sensore per "${abbinandoFrigo.nome}"` : 'Gestione sensori temperatura'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={loadSensoriTuya}
                  disabled={loadingSensori}
                  className="p-2 bg-slate-800 lg:bg-gray-100 hover:bg-slate-700 lg:hover:bg-gray-200 rounded-xl"
                >
                  <RefreshCw className={`h-4 w-4 text-white lg:text-gray-600 ${loadingSensori ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => { setShowSensoriModal(false); setAbbinandoFrigo(null); }}
                  className="p-2 bg-slate-800 lg:bg-gray-100 hover:bg-slate-700 lg:hover:bg-gray-200 rounded-xl"
                >
                  <X className="h-5 w-5 text-white lg:text-gray-600" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {/* Se stiamo abbinando un frigo, mostra solo i sensori disponibili */}
              {abbinandoFrigo ? (
                <>
                  <div className="p-3 bg-purple-500/20 border border-purple-500/30 rounded-xl">
                    <p className="text-purple-300 lg:text-purple-700 text-sm">
                      🔗 Abbinando sensore a: <strong>{abbinandoFrigo.nome}</strong>
                    </p>
                  </div>
                  
                  {loadingSensori ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent" />
                    </div>
                  ) : sensoriTuya.length === 0 ? (
                    <div className="text-center py-8">
                      <Wifi className="h-12 w-12 text-slate-500 mx-auto mb-3" />
                      <p className="text-slate-400 lg:text-gray-500">Nessun sensore trovato</p>
                      <p className="text-slate-500 lg:text-gray-400 text-xs mt-1">Collega i sensori nell&apos;app Ristorantissimo</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {sensoriTuya.map((sensore) => (
                        <div
                          key={sensore.id}
                          className="p-3 bg-slate-800/70 lg:bg-gray-50 rounded-xl border border-slate-700 lg:border-gray-200"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Thermometer className="h-5 w-5 text-cyan-400" />
                              <span className="font-medium text-white lg:text-gray-900">{sensore.nome}</span>
                              {sensore.online ? (
                                <span className="text-[10px] bg-green-500/30 text-green-400 px-1.5 py-0.5 rounded">ONLINE</span>
                              ) : (
                                <span className="text-[10px] bg-red-500/30 text-red-400 px-1.5 py-0.5 rounded">OFFLINE</span>
                              )}
                            </div>
                            <div className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded ${
                              sensore.batteria === 'high' ? 'bg-green-500/30 text-green-400' :
                              sensore.batteria === 'middle' ? 'bg-yellow-500/30 text-yellow-400' :
                              sensore.batteria === 'low' ? 'bg-red-500/30 text-red-400' :
                              'bg-slate-500/30 text-slate-400'
                            }`}>
                              <Battery className="h-3 w-3" />
                              {sensore.batteria === 'high' ? '100%' : 
                               sensore.batteria === 'middle' ? '50%' : 
                               sensore.batteria === 'low' ? '20%' : 
                               sensore.batteria || '?'}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm mb-3">
                            {sensore.temperatura != null && (
                              <div className="flex items-center gap-1">
                                <span className="text-slate-400">Interno:</span>
                                <span className="text-cyan-400 font-bold">{sensore.temperatura}°C</span>
                              </div>
                            )}
                            {sensore.temperatura_esterna != null && (
                              <div className="flex items-center gap-1">
                                <span className="text-slate-400">Sonda:</span>
                                <span className="text-blue-400 font-bold">{sensore.temperatura_esterna}°C</span>
                              </div>
                            )}
                            {sensore.umidita != null && (
                              <div className="flex items-center gap-1">
                                <span className="text-slate-400">Umidità:</span>
                                <span className="text-purple-400 font-bold">{sensore.umidita}%</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex gap-2">
                            <button
                              onClick={() => abbinaSensore(sensore, false)}
                              className="flex-1 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-xs font-medium"
                            >
                              Usa Temp. Interna ({sensore.temperatura != null ? sensore.temperatura + '°C' : 'N/A'})
                            </button>
                            {sensore.temperatura_esterna != null && (
                              <button
                                onClick={() => abbinaSensore(sensore, true)}
                                className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium"
                              >
                                Usa Sonda ({sensore.temperatura_esterna}°C)
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <button
                    onClick={() => setAbbinandoFrigo(null)}
                    className="w-full py-2 bg-slate-700 lg:bg-gray-200 text-white lg:text-gray-700 rounded-xl text-sm"
                  >
                    Annulla abbinamento
                  </button>
                </>
              ) : (
                <>
                  {/* Mostra frigoriferi con stato sensori */}
                  <div className="space-y-2">
                    <p className="text-xs text-slate-400 lg:text-gray-500 font-medium">Frigoriferi e Sensori Abbinati</p>
                    
                    {frigoriferi.map((frigo) => (
                      <div
                        key={frigo.id}
                        className="p-3 bg-slate-800/70 lg:bg-gray-50 rounded-xl border border-slate-700 lg:border-gray-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Thermometer className={`h-5 w-5 ${frigo.tipo === 'congelatore' ? 'text-blue-400' : 'text-cyan-400'}`} />
                            <div>
                              <span className="font-medium text-white lg:text-gray-900">{frigo.nome}</span>
                              <p className="text-xs text-slate-400 lg:text-gray-500">
                                {frigo.tipo === 'congelatore' ? '❄️ Congelatore' : '🧊 Frigorifero'} • {frigo.temp_min}°C - {frigo.temp_max}°C
                              </p>
                            </div>
                          </div>
                          
                          {frigo.sensore_tuya_id ? (
                            <div className="flex items-center gap-2">
                              <div className="text-right">
                                <div className="flex items-center gap-2 justify-end">
                                  <p className="text-xs text-green-400 flex items-center gap-1">
                                    <Wifi className="h-3 w-3" />
                                    {frigo.sensore_tuya_nome}
                                  </p>
                                  {frigo.sensore_batteria && (
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded flex items-center gap-0.5 ${
                                      frigo.sensore_batteria === 'high' ? 'bg-green-500/30 text-green-400' :
                                      frigo.sensore_batteria === 'middle' ? 'bg-yellow-500/30 text-yellow-400' :
                                      'bg-red-500/30 text-red-400'
                                    }`}>
                                      <Battery className="h-2.5 w-2.5" />
                                      {frigo.sensore_batteria === 'high' ? '100%' : frigo.sensore_batteria === 'middle' ? '50%' : '20%'}
                                    </span>
                                  )}
                                </div>
                                {frigo.temperatura_attuale !== undefined && (
                                  <p className={`text-lg font-bold ${
                                    frigo.temperatura_attuale < frigo.temp_min || frigo.temperatura_attuale > frigo.temp_max
                                      ? 'text-red-400'
                                      : 'text-cyan-400'
                                  }`}>{frigo.temperatura_attuale}°C</p>
                                )}
                              </div>
                              <button
                                onClick={() => rimuoviSensore(frigo.id)}
                                className="p-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg"
                                title="Rimuovi sensore"
                              >
                                <Unlink className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setAbbinandoFrigo(frigo);
                                loadSensoriTuya();
                              }}
                              className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-medium flex items-center gap-1"
                            >
                              <Link2 className="h-3 w-3" />
                              Abbina Sensore
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Sensori disponibili */}
                  <div className="space-y-2 mt-4">
                    <p className="text-xs text-slate-400 lg:text-gray-500 font-medium">Sensori Ristorantissimo Disponibili ({sensoriTuya.length})</p>
                    
                    {loadingSensori ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-purple-500 border-t-transparent" />
                      </div>
                    ) : sensoriTuya.length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-slate-500 text-sm">Nessun sensore trovato</p>
                      </div>
                    ) : (
                      sensoriTuya.map((sensore) => (
                        <div
                          key={sensore.id}
                          className="p-2 bg-slate-700/50 lg:bg-gray-100 rounded-lg flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2">
                            <Wifi className={`h-4 w-4 ${sensore.online ? 'text-green-400' : 'text-red-400'}`} />
                            <span className="text-sm text-white lg:text-gray-700">{sensore.nome}</span>
                          </div>
                          <div className="flex items-center gap-3 text-xs">
                            {sensore.temperatura_esterna != null && (
                              <span className="text-cyan-400 font-medium">{sensore.temperatura_esterna}°C</span>
                            )}
                            {sensore.batteria && (
                              <span className={`flex items-center gap-1 ${
                                sensore.batteria === 'high' ? 'text-green-400' :
                                sensore.batteria === 'middle' ? 'text-yellow-400' :
                                'text-red-400'
                              }`}>
                                <Battery className="h-3 w-3" />
                                {sensore.batteria === 'high' ? '100%' : 
                                 sensore.batteria === 'middle' ? '50%' : 
                                 sensore.batteria === 'low' ? '20%' : sensore.batteria}
                              </span>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  
                  {/* Bottone lettura automatica manuale */}
                  {frigoriferi.some(f => f.sensore_tuya_id) && (
                    <button
                      onClick={eseguiLetturaAutomatica}
                      className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium flex items-center justify-center gap-2"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Esegui Lettura Automatica Ora
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Impostazioni Compilazione Automatica */}
      {showImpostazioniAuto && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1e293b] lg:bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-700 lg:border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-indigo-400" />
                <h3 className="font-semibold text-white lg:text-gray-900">Compilazione Automatica</h3>
              </div>
              <button onClick={() => setShowImpostazioniAuto(false)} className="text-slate-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Toggle abilitato */}
              <div className="flex items-center justify-between p-3 bg-slate-800/50 lg:bg-gray-100 rounded-xl">
                <div>
                  <p className="font-medium text-white lg:text-gray-900">Compilazione Automatica</p>
                  <p className="text-xs text-slate-400 lg:text-gray-500">Registra temperature dai sensori</p>
                </div>
                <button
                  onClick={() => setImpostazioniAuto({...impostazioniAuto, abilitato: !impostazioniAuto.abilitato})}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    impostazioniAuto.abilitato ? 'bg-green-500' : 'bg-slate-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                    impostazioniAuto.abilitato ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>

              {/* Ora di esecuzione */}
              <div>
                <label className="block text-sm font-medium text-slate-300 lg:text-gray-700 mb-1">
                  Ora di esecuzione giornaliera
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="time"
                    value={impostazioniAuto.ora}
                    onChange={(e) => setImpostazioniAuto({...impostazioniAuto, ora: e.target.value})}
                    className="flex-1 px-3 py-2 bg-slate-800 lg:bg-white border border-slate-700 lg:border-gray-300 rounded-xl text-white lg:text-gray-900"
                  />
                  {impostazioniAuto.ora_corrente_italia && (
                    <span className="text-xs text-slate-400 lg:text-gray-500">
                      Ora attuale: <strong className="text-indigo-400">{impostazioniAuto.ora_corrente_italia}</strong>
                    </span>
                  )}
                </div>
              </div>

              {/* Operatore default */}
              <div>
                <label className="block text-sm font-medium text-slate-300 lg:text-gray-700 mb-1">
                  Nome operatore (registro)
                </label>
                <input
                  type="text"
                  value={impostazioniAuto.operatore_default}
                  onChange={(e) => setImpostazioniAuto({...impostazioniAuto, operatore_default: e.target.value})}
                  placeholder="Sistema Automatico"
                  className="w-full px-3 py-2 bg-slate-800 lg:bg-white border border-slate-700 lg:border-gray-300 rounded-xl text-white lg:text-gray-900"
                />
              </div>

              {/* Giorni attivi */}
              <div>
                <label className="block text-sm font-medium text-slate-300 lg:text-gray-700 mb-2">
                  Giorni attivi
                </label>
                <div className="flex flex-wrap gap-2">
                  {['lun', 'mar', 'mer', 'gio', 'ven', 'sab', 'dom'].map((giorno) => (
                    <button
                      key={giorno}
                      onClick={() => {
                        const giorni = impostazioniAuto.giorni_attivi || [];
                        const newGiorni = giorni.includes(giorno) 
                          ? giorni.filter(g => g !== giorno)
                          : [...giorni, giorno];
                        setImpostazioniAuto({...impostazioniAuto, giorni_attivi: newGiorni});
                      }}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        (impostazioniAuto.giorni_attivi || []).includes(giorno)
                          ? 'bg-indigo-600 text-white'
                          : 'bg-slate-700 lg:bg-gray-200 text-slate-300 lg:text-gray-600'
                      }`}
                    >
                      {giorno.charAt(0).toUpperCase() + giorno.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notifica anomalie */}
              <div className="flex items-center justify-between p-3 bg-slate-800/50 lg:bg-gray-100 rounded-xl">
                <div>
                  <p className="font-medium text-white lg:text-gray-900 text-sm">Notifica anomalie</p>
                  <p className="text-xs text-slate-400 lg:text-gray-500">Avvisa se fuori range</p>
                </div>
                <button
                  onClick={() => setImpostazioniAuto({...impostazioniAuto, notifica_anomalie: !impostazioniAuto.notifica_anomalie})}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    impostazioniAuto.notifica_anomalie ? 'bg-green-500' : 'bg-slate-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                    impostazioniAuto.notifica_anomalie ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>

              {/* Sensore che non risponde: si scrive la temperatura del display (scelta utente) */}
              <div className="flex items-center justify-between gap-3 p-3 bg-slate-800/50 lg:bg-gray-100 rounded-xl">
                <div>
                  <p className="font-medium text-white lg:text-gray-900 text-sm">Sensore che non risponde</p>
                  <p className="text-xs text-slate-400 lg:text-gray-500">Avvisa subito e fai scrivere la temperatura letta sul display</p>
                </div>
                <button
                  onClick={() => setImpostazioniAuto({...impostazioniAuto, temperatura_a_mano: impostazioniAuto.temperatura_a_mano === false})}
                  data-testid="impostazioni-temperatura-a-mano-toggle"
                  aria-pressed={impostazioniAuto.temperatura_a_mano !== false}
                  className={`w-12 h-6 shrink-0 rounded-full transition-colors ${
                    impostazioniAuto.temperatura_a_mano !== false ? 'bg-green-500' : 'bg-slate-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                    impostazioniAuto.temperatura_a_mano !== false ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>

              {/* Info sensori */}
              <div className="p-3 bg-indigo-500/10 border border-indigo-500/30 rounded-xl">
                <p className="text-xs text-indigo-300 lg:text-indigo-600">
                  <strong>Nota:</strong> La compilazione automatica funziona solo per i frigoriferi con sensore Ristorantissimo abbinato. 
                  Attualmente hai {frigoriferi.filter(f => f.sensore_tuya_id).length} frigoriferi con sensore.
                </p>
              </div>

              {/* Pulsanti */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowImpostazioniAuto(false)}
                  data-testid="impostazioni-auto-annulla"
                  className="flex-1 py-2.5 bg-slate-700 lg:bg-gray-200 text-white lg:text-gray-700 rounded-xl font-medium"
                >
                  Annulla
                </button>
                <button
                  onClick={salvaImpostazioniAuto}
                  disabled={savingImpostazioni}
                  data-testid="impostazioni-auto-salva"
                  className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium flex items-center justify-center gap-2"
                >
                  {savingImpostazioni ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  Salva
                </button>
              </div>

              {/* Esegui ora */}
              <button
                onClick={eseguiCompilazioneAuto}
                disabled={eseguendoAuto || frigoriferi.filter(f => f.sensore_tuya_id).length === 0}
                className="w-full py-3 bg-green-600 hover:bg-green-700 disabled:bg-slate-600 text-white rounded-xl font-medium flex items-center justify-center gap-2"
              >
                {eseguendoAuto ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Compilazione in corso...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    Esegui Compilazione Adesso
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Stampa Etichette Frigoriferi */}
      {showEtichetteModal && (
        <div className="fixed inset-0 bg-black/70 flex items-end lg:items-center justify-center z-[130]">
          <div className="bg-white rounded-t-2xl lg:rounded-2xl w-full lg:max-w-md max-h-[85vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-teal-50 to-cyan-50">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <Printer className="h-5 w-5 text-teal-600" />
                  Stampa Etichette
                </h3>
                <button 
                  onClick={() => setShowEtichetteModal(false)} 
                  className="p-1.5 hover:bg-white/50 rounded-lg"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              <p className="text-xs text-gray-600 mt-1">Seleziona i frigoriferi da stampare</p>
            </div>
            
            {/* Lista frigoriferi con checkbox */}
            <div className="p-4 overflow-y-auto max-h-[50vh] bg-white">
              {/* Seleziona tutto */}
              <div className="mb-3 pb-3 border-b border-gray-200">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={frigoriferiSelezionatiEtichette.length === frigoriferi.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFrigoriferiSelezionatiEtichette(frigoriferi.map(f => f.id));
                      } else {
                        setFrigoriferiSelezionatiEtichette([]);
                      }
                    }}
                    className="w-5 h-5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="font-semibold text-gray-900">Seleziona tutti ({frigoriferi.length})</span>
                </label>
              </div>
              
              {/* Lista frigoriferi */}
              <div className="space-y-2">
                {frigoriferi.map((frigo) => (
                  <label 
                    key={frigo.id}
                    className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                      frigoriferiSelezionatiEtichette.includes(frigo.id)
                        ? 'bg-teal-100 border-2 border-teal-400'
                        : 'bg-gray-100 hover:bg-gray-200 border-2 border-gray-200'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={frigoriferiSelezionatiEtichette.includes(frigo.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFrigoriferiSelezionatiEtichette([...frigoriferiSelezionatiEtichette, frigo.id]);
                        } else {
                          setFrigoriferiSelezionatiEtichette(frigoriferiSelezionatiEtichette.filter(id => id !== frigo.id));
                        }
                      }}
                      className="w-5 h-5 rounded border-gray-400 text-teal-600 focus:ring-teal-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{frigo.tipo === 'congelatore' ? '❄️' : '🧊'}</span>
                        <span className="font-bold text-gray-900">{frigo.nome}</span>
                      </div>
                      <p className="text-sm text-gray-700 font-medium">
                        {frigo.temp_min}°C / {frigo.temp_max}°C
                        {frigo.posizione && ` • ${frigo.posizione}`}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Pulsanti azione */}
            <div className="p-4 border-t border-gray-100 space-y-2 bg-white">
              {/* Stampa selezionati - Apri PDF direttamente */}
              <button
                onClick={async () => {
                  const tenantDb = localStorage.getItem('tenant_db');
                  let url;
                  let filename;
                  if (frigoriferiSelezionatiEtichette.length === 1) {
                    url = `${API}/gestione/haccp/frigoriferi/etichetta-pdf/${frigoriferiSelezionatiEtichette[0]}?tenant=${tenantDb}`;
                    filename = 'etichetta_frigorifero_100x50mm.pdf';
                  } else {
                    const ids = frigoriferiSelezionatiEtichette.join(',');
                    url = `${API}/gestione/haccp/frigoriferi/etichette-pdf-selezionate?tenant=${tenantDb}&ids=${ids}`;
                    filename = `etichette_${frigoriferiSelezionatiEtichette.length}_100x50mm.pdf`;
                  }
                  
                  try {
                    // Scarica il PDF come blob
                    const response = await fetch(url);
                    const blob = await response.blob();
                    const file = new File([blob], filename, { type: 'application/pdf' });
                    
                    // Prova Web Share API (funziona su mobile)
                    if (navigator.canShare && navigator.canShare({ files: [file] })) {
                      await navigator.share({
                        files: [file],
                        title: 'Etichette Frigoriferi 100x50mm',
                        text: 'Stampa le etichette con dimensioni 100x50mm'
                      });
                    } else {
                      // Fallback: apri in nuova finestra browser
                      const blobUrl = URL.createObjectURL(blob);
                      window.open(blobUrl, '_blank');
                      // Pulisci dopo un po'
                      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                    }
                  } catch (error) {
                    console.error('Errore:', error);
                    // Fallback finale: download diretto
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }
                  
                  setShowEtichetteModal(false);
                }}
                disabled={frigoriferiSelezionatiEtichette.length === 0}
                className="w-full py-4 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2"
              >
                <Printer className="h-6 w-6" />
                {frigoriferiSelezionatiEtichette.length === 0 
                  ? 'Seleziona almeno un frigorifero'
                  : frigoriferiSelezionatiEtichette.length === 1
                    ? 'Condividi/Stampa PDF'
                    : `Condividi/Stampa ${frigoriferiSelezionatiEtichette.length} Etichette`
                }
              </button>
              
              {/* Annulla */}
              <button
                onClick={() => setShowEtichetteModal(false)}
                className="w-full py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl font-medium"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FrigoriferiSection;
